import { Box, Paper } from '@mui/material';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { FC, useMemo } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi, PatientLanguage } from '../../../../../../services/api-client/contact-api';
import AccordionSection from '../../../../../shared/accordion/accordion-section';
import AccordionSectionType from '../../../../../shared/accordion/accordion-section-type';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { usePatientProfileWidget } from '../../hooks/use-patient-profile-widget';
import { WidgetRoutePath } from '../../navigation/route-config';
import { ViewContent } from '../shared/view-content/view-content';
import { Details, Dropdown, DropDownText } from './comm-preferences.styles';
import CampaignCommunicationStatus from './shared/campaign-communication-status/campaign-communication-status';
import { CommunicationStatus } from './types/communication-status';
import { getCustomizedAppointmentCount } from './utils/get-customized-appointment-count';
import { getCustomizedDMCount } from './utils/get-customized-dm-count';
import { getCustomizedEmailCount } from './utils/get-customized-email-count';
import { getCustomizedPhoneCount } from './utils/get-customized-phone-count';
import { getCustomizedTextCount } from './utils/get-customized-text-count';
import { getDefaultFamilyMember } from './utils/get-default-family-member';
import { getDMStatus } from './utils/get-dm-status';
import { getEmailStatus } from './utils/get-email-status';
import { getPhoneStatus } from './utils/get-phone-status';
import { getTextStatus } from './utils/get-text-status';
import { isAddressEmpty } from './utils/is-address-empty';

export const CommPreferencesView: FC = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { goTo } = usePatientProfileWidget();
  const { patientPreferences, patientInfo, refreshPatientPreferences } = usePatientProfile();
  const contactApi = useMemo(() => new ContactApi(config, oidcService), [config, oidcService]);
  const langOptions: PatientLanguage[] = [PatientLanguage.English, PatientLanguage.Spanish];

  const patientAddress = () => {
    const patientIndex = patientInfo.familyMembers?.findIndex((familyMember) => familyMember.id === patientInfo.id);
    return patientIndex !== -1 ? patientInfo.familyMembers![patientIndex!].address : undefined;
  };

  const updateFamilyOptions = async (familyMemberId: number) => {
    await contactApi.patchPatientPreferences(patientInfo.id, {
      ...patientPreferences,
      familyCommunicationReceiverId: familyMemberId
    });
    await refreshPatientPreferences();
  };

  const updateLanguageOptions = async (language: PatientLanguage) => {
    await contactApi.patchPatientPreferences(patientInfo.id, {
      ...patientPreferences,
      language: language
    });
    await refreshPatientPreferences();
  };

  const DropDownPaper = (props: React.HTMLAttributes<HTMLElement>) => {
    return <Paper elevation={8} style={{ background: '#FFFFFF' }} {...props} />;
  };

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        <Details>
          <AccordionSectionType header="GENERAL"></AccordionSectionType>
          <AccordionSection
            showNavArrow={true}
            header="Appointment Reminders"
            onClick={() => goTo(WidgetRoutePath.COMM_PREFERENCES_REMINDERS)}
          >
            <label>{getCustomizedAppointmentCount(patientPreferences)} of 5 customized</label>
          </AccordionSection>
          {patientInfo.familyMembers && (
            <AccordionSection header="Family Messaging">
              <Dropdown
                autoComplete={false}
                disableClearable
                sx={{ WebkitTextFillColor: '#000000' }}
                PaperComponent={DropDownPaper}
                disablePortal
                defaultValue={getDefaultFamilyMember(patientPreferences, patientInfo.familyMembers)}
                size="small"
                id="family-options-dropdown"
                options={patientInfo.familyMembers}
                onChange={(item, value: any) => updateFamilyOptions(value.id)}
                getOptionLabel={(option: any) =>
                  `${option.firstName} ${option.lastName} ${option.isResponsibleParty ? ' (RP)' : ''}`
                }
                renderOption={(props, option: any) => (
                  <Box component="li" {...props} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
                    {`${option?.firstName} ${option?.lastName} ${option?.isResponsibleParty ? ' (RP)' : ''}`}
                  </Box>
                )}
                renderInput={(params) => (
                  <DropDownText {...params} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }} variant="outlined" />
                )}
              />
            </AccordionSection>
          )}
          <AccordionSection header="Preferred Language">
            <Dropdown
              autoComplete={false}
              disableClearable
              sx={{ WebkitTextFillColor: '#000000' }}
              PaperComponent={DropDownPaper}
              disablePortal
              defaultValue={patientPreferences.language}
              size="small"
              id="language-options-dropdown"
              options={langOptions}
              onChange={(item, value: any) => updateLanguageOptions(value)}
              getOptionLabel={(option: any) => option}
              renderOption={(props, option: any) => (
                <Box component="li" {...props} sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
                  {option}
                </Box>
              )}
              renderInput={(params) => <DropDownText {...params} variant="outlined" />}
            />
          </AccordionSection>
          <AccordionSectionType header="CAMPAIGN COMMUNICATIONS"></AccordionSectionType>
          <AccordionSection
            header="Email"
            showNavArrow={true}
            onClick={() => goTo(WidgetRoutePath.COMM_PREFERENCES_EMAIL)}
          >
            <CampaignCommunicationStatus
              status={getEmailStatus(patientPreferences)}
              label={`${getCustomizedEmailCount(patientPreferences)} of 9 active`}
            />
          </AccordionSection>
          <AccordionSection
            header="Text Message"
            showNavArrow={true}
            onClick={() => goTo(WidgetRoutePath.COMM_PREFERENCES_TEXT_MESSAGE)}
          >
            <CampaignCommunicationStatus
              status={getTextStatus(patientPreferences)}
              label={`${getCustomizedTextCount(patientPreferences)} of 6 active`}
            />
          </AccordionSection>
          <AccordionSection
            header="Direct Mail"
            disabled={isAddressEmpty(patientAddress())}
            showNavArrow={true}
            onClick={() => goTo(WidgetRoutePath.COMM_PREFERENCES_DIRECT_MAIL)}
          >
            <CampaignCommunicationStatus
              status={isAddressEmpty(patientAddress()) ? CommunicationStatus.none : getDMStatus(patientPreferences)}
              label={
                isAddressEmpty(patientAddress())
                  ? 'No Address Listed'
                  : `${getCustomizedDMCount(patientPreferences)} of 7 active`
              }
            />
          </AccordionSection>
          <AccordionSection
            header="Phone Calls"
            showNavArrow={true}
            onClick={() => goTo(WidgetRoutePath.COMM_PREFERENCES_PHONE_CALL)}
          >
            <CampaignCommunicationStatus
              status={getPhoneStatus(patientPreferences)}
              label={`${getCustomizedPhoneCount(patientPreferences)} of 1 active`}
            />
          </AccordionSection>
        </Details>
      </ViewContent>
    </>
  );
};
